import { Text, Image, Box, Radio, Group, Badge } from "@mantine/core";
import { useAtlasProduct } from "@labeling-tools/api/hooks";
import { useDispatch } from "react-redux";
import {
  RootState,
  setPlanogramModalSource,
  setPlanogramModalUsed,
  setPlanogramSelectedProductId,
  setProductMatchConfirmed,
  setSelectedProductId,
} from "../store";
import { ActionStatusBadge } from "./ActionStatusBadge";
import { JobTaskDetailSchema } from "@augmodo/lebron-browser-client";
import { ImageLoading } from "@labeling-tools/ui-components";
import { QaQuestionAction } from "./QaQuestionAction";
import { ViewMode } from "@/types";
import { useAppSelector } from "@/store/hooks";
import { useTimeTracking } from "@/contexts/TimeTrackingContext";
import { IconParkingCircle } from "@tabler/icons-react";
import { useMemo } from "react";

export const ProductMatchCard = ({
  productId,
  mode,
  task,
  topPick,
  planogramId,
  roleLabel,
  roleColor = "gray",
  hideActionStatus = false,
}: {
  mode?: ViewMode;
  productId: string | null;
  task: JobTaskDetailSchema;
  topPick?: boolean;
  planogramId: string | null;
  roleLabel?: string;
  roleColor?: string;
  hideActionStatus?: boolean;
}) => {
  const dispatch = useDispatch();
  const { recordQ3Interaction } = useTimeTracking();
  // redux state vars
  const selectedProductId = useAppSelector(
    (state: RootState) => state.oos.selectedProductId,
  );

  // query atlas product
  const { data: atlasProduct, isLoading: atlasProductLoading } =
    useAtlasProduct(productId || null);

  // handle when "p" icon is clicked to launch planogram modal
  const handlePlanogramSelect = () => {
    dispatch(setPlanogramSelectedProductId(productId || null));
    dispatch(setPlanogramModalSource("product"));
    dispatch(setPlanogramModalUsed(true));
  };

  const radioChecked = selectedProductId === productId;
  const blockRender = useMemo(() => {
    if (!atlasProduct) return true;
    const matchesPlanogram = atlasProduct.attached_planograms?.some(
      (p) => p.id === planogramId,
    );
    return !matchesPlanogram && !topPick && !radioChecked && mode === "viewer";
  }, [atlasProduct, planogramId, topPick, radioChecked, mode]);

  if (blockRender) return null;
  const isRejected = roleLabel === "Predicted (Rejected)";

  // Q3 (product match) is only meaningful once Q2 (item detection) is "Yes".
  // When Q2 is unanswered or answered "No", force the Q3 badge to "Unanswered".
  const isQaMode =
    mode === "confirmation-qa" || mode === "confirmation-qa-feedback";
  const q2Confirmed = isQaMode
    ? task.data?.confirmation_result?.item_detection_confirmed
    : task.result?.item_detection_confirmed; // admin reads task.result
  const q3Unanswered = q2Confirmed !== true;

  return (
    <div className="w-full" style={{ opacity: isRejected ? 0.7 : 1 }}>
      <div className="relative pt-[30px]">
        {/* icon button to launch planogram modal */}
        {!!atlasProduct?.attached_planograms &&
          atlasProduct?.attached_planograms?.length > 0 && (
            <div className="absolute top-2 left-2 cursor-pointer">
              <IconParkingCircle
                className="text-gray-500"
                onClick={handlePlanogramSelect}
              />
            </div>
          )}

        {roleLabel ? (
          <Badge
            variant="filled"
            color={roleColor}
            className="absolute top-0 right-0"
          >
            {roleLabel}
          </Badge>
        ) : (
          topPick && (
            <Badge variant="outline" className="absolute top-0 right-0">
              Likely Match
            </Badge>
          )
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            position: "relative",
          }}
        >
          <div className="relative w-full flex justify-center">
            <Box
              className="relative"
              style={{
                width: "calc(100% - 60px)",
                // height: '300px',
                // overflow: 'hidden',
                aspectRatio: "1/1",
              }}
            >
              {atlasProductLoading && <ImageLoading />}
              <Image
                src={atlasProduct?.image_url}
                alt="Product"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </Box>
          </div>
          <Text style={{ color: "black", textAlign: "center" }}>
            {atlasProduct?.description}
          </Text>
          <div className="flex flex-col" style={{ marginTop: "auto" }}>
            {/* "viewer" mode shows radios */}
            {mode !== "admin" &&
              mode !== "confirmation-qa" &&
              mode !== "confirmation-qa-feedback" && (
                <Group mb="sm" justify="center">
                  <Radio
                    value={productId}
                    label="Select Product"
                    checked={radioChecked}
                    onChange={(event) => {
                      recordQ3Interaction();
                      dispatch(setProductMatchConfirmed(true));
                      dispatch(setSelectedProductId(event.currentTarget.value));
                    }}
                  />
                </Group>
              )}

            {/* "admin" mode shows action status */}
            {mode === "admin" && !hideActionStatus && (
              <ActionStatusBadge
                resultField="product_match_confirmed"
                task={task}
                forceUnanswered={q3Unanswered}
              />
            )}
            {/* "confirmation-qa" mode shows action status from data */}
            {(mode === "confirmation-qa" ||
              mode === "confirmation-qa-feedback") &&
              !hideActionStatus && (
                <>
                  <ActionStatusBadge
                    resultField="product_match_confirmed"
                    task={{
                      ...task,
                      result: task.data?.confirmation_result || ({} as any),
                    }}
                    forceUnanswered={q3Unanswered}
                  />
                  <QaQuestionAction questionNumber={3} task={task} />
                </>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};
